import React from 'react';
import {ApiRequestParams, PaginatedResult} from '../api/types';
import useApiClient from './useApiClient';

interface Props<T> {
  data: T[];
  setData: (t: T[]) => void;
  page: number;
  setPage: (p: number) => void;
  endReached: boolean;
  setEndReached: (e: boolean) => void;
  resetAll: () => void;
  load: (apiRequest: ApiRequestParams, reset: boolean) => void;
  loading: boolean;
}

function usePagination<T>(): Props<T> {
  const [data, setData] = React.useState<T[]>([]);
  const [page, setPage] = React.useState(1);
  const [endReached, setEndReached] = React.useState(false);

  const {fetch, loading} = useApiClient();

  // The page to request is read at execution time from a ref (not captured
  // at render time) so that a `load` fired while a previous load is still
  // settling can never re-request an already-loaded page.
  const pageRef = React.useRef(page);

  // Keep the ref in sync even when consumers call setPage directly.
  const setPageSynced = React.useCallback((p: number) => {
    pageRef.current = p;
    setPage(p);
  }, []);

  // In-flight guard: concurrent non-reset loads are coalesced (ignored while
  // one is pending). Reset loads are never blocked — instead they bump the
  // request generation so any in-flight response is discarded when it lands.
  const inFlightRef = React.useRef(false);
  const generationRef = React.useRef(0);

  const resetAll = React.useCallback(() => {
    // Invalidate any in-flight request so its late response cannot clobber
    // the reset state.
    generationRef.current += 1;
    inFlightRef.current = false;
    setPageSynced(1);
    setData([]);
    setEndReached(false);
  }, [setPageSynced]);

  const load = React.useCallback(
    (apiRequest: ApiRequestParams, reset: boolean) => {
      if (!reset && inFlightRef.current) {
        // A page load is already pending — coalesce duplicate triggers
        // (e.g. onEndReached firing repeatedly during a slow request).
        return;
      }
      if (reset) {
        // A reset load supersedes whatever is in flight and re-opens
        // pagination for lists that had previously reached their end.
        generationRef.current += 1;
        setEndReached(false);
      }
      inFlightRef.current = true;
      const generation = generationRef.current;
      const requestPage = reset ? 1 : pageRef.current;
      const params = apiRequest.params
        ? {...apiRequest.params, page: requestPage}
        : {page: requestPage};
      fetch({...apiRequest, params})
        .then((response: PaginatedResult<T[]>) => {
          if (generation !== generationRef.current) {
            // Superseded by a reset while in flight — discard the stale
            // response.
            return;
          }
          if (reset) {
            setData(response.results);
          } else {
            setData((current) => current.concat(response.results));
          }
          setPageSynced(requestPage + 1);
          if (!response.next) {
            setEndReached(true);
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          if (generation === generationRef.current) {
            inFlightRef.current = false;
          }
        });
    },
    [fetch, setPageSynced],
  );

  return {
    data,
    setData,
    page,
    setPage: setPageSynced,
    endReached,
    setEndReached,
    resetAll,
    load,
    loading,
  };
}

export default usePagination;
